import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { useSupabaseAuth } from '@/contexts/SupabaseAuthContext';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import WhatsAppButton from '@/components/WhatsAppButton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { UserPlus, BookOpen, CheckCircle } from 'lucide-react';
import { trackLead, trackCompleteRegistration } from '@/lib/metaPixel';

const Signup: React.FC = () => {
  const { user, signUp, isLoading } = useSupabaseAuth();
  const navigate = useNavigate();
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [fullName, setFullName] = React.useState('');
  const [mobile, setMobile] = React.useState('');
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  useEffect(() => {
    // Meta Pixel - Lead event for Signup page
    trackLead({
      content_name: 'Signup Page',
      content_category: 'Registration',
    });
  }, []);

  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const { error } = await signUp(email, password, fullName, mobile);
      if (error) {
        toast.error(error.message);
      } else {
        // Meta Pixel - CompleteRegistration
        trackCompleteRegistration({
          content_name: 'User Registration',
          status: 'success',
        });
        toast.success('Account created successfully!');
        navigate('/dashboard');
      }
    } catch (err) {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Sign Up - Create Your TOPPERS BUDDY Account | Class 10 Science Notes</title>
        <meta name="description" content="Sign up for TOPPERS BUDDY to get access to the best class 10 science notes. Create your account and start your board exam preparation today." />
        <meta name="keywords" content="toppers buddy signup, create account, class 10 notes registration, student registration" />
        <link rel="canonical" href="https://toppersbuddy.lovable.app/signup" />
        <meta property="og:title" content="Sign Up - Create Your TOPPERS BUDDY Account" />
        <meta property="og:url" content="https://toppersbuddy.lovable.app/signup" />
      </Helmet>

      <div className="flex min-h-screen flex-col bg-background">
        <Header />
        <main role="main" aria-label="Sign Up" className="flex-1 py-12">
          <div className="container max-w-lg mx-auto px-4">
            <Card className="border-border/50 shadow-xl">
              <CardHeader className="text-center pb-2">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary/10">
                  <UserPlus className="h-8 w-8 text-primary" />
                </div>
                <CardTitle className="text-2xl font-bold">Create Your Account</CardTitle>
                <CardDescription>Join 1,300+ students preparing for board exams</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSignup} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="fullName">Full Name</Label>
                    <Input
                      id="fullName"
                      type="text"
                      placeholder="Enter your full name"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="mobile">WhatsApp Number</Label>
                    <Input
                      id="mobile"
                      type="tel"
                      placeholder="Enter your WhatsApp number"
                      value={mobile}
                      onChange={(e) => setMobile(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="Enter your email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      placeholder="Create a password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={6}
                    />
                  </div>
                  <Button type="submit" className="w-full" disabled={isSubmitting}>
                    {isSubmitting ? 'Creating Account...' : 'Sign Up'}
                  </Button>
                </form>

                <div className="mt-6 space-y-3">
                  <p className="text-center text-sm text-muted-foreground">
                    What you get after signup:
                  </p>
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span>Access to buy toppers notes at ₹149</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span>Personal dashboard to track purchases</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <BookOpen className="h-4 w-4 text-primary" />
                      <span>Instant notes access after payment</span>
                    </div>
                  </div>
                </div>

                <p className="mt-6 text-center text-sm text-muted-foreground">
                  Already have an account?{' '}
                  <a href="/auth" className="text-primary hover:underline">
                    Login here
                  </a>
                </p>
              </CardContent>
            </Card>
          </div>
        </main>
        <Footer />
      </div>
      
      <WhatsAppButton />
    </>
  );
};

export default Signup;
